/**
 * Catálogo de estilos (temas) para el portfolio público.
 *
 * Cada tema define una combinación coherente de colores, tipografía, radios y
 * estilo de cabecera. El administrador puede elegir uno y, opcionalmente,
 * sobrescribir el color de marca y la tipografía sin complicarse.
 */
import type {
  PortfolioConfig,
  PortfolioFont,
  PortfolioGrouping,
  PortfolioLayout,
  PortfolioTheme,
} from '../types';

export interface PortfolioThemePreset {
  id: PortfolioTheme;
  name: string;
  hint: string;
  accent: string;
  bg: string;
  surface: string;
  text: string;
  muted: string;
  border: string;
  radius: string;
  font: PortfolioFont;
  hero: 'overlay' | 'banner' | 'minimal';
  dark: boolean;
}

export const PORTFOLIO_FONTS: Record<PortfolioFont, { label: string; heading: string; body: string }> = {
  sans: {
    label: 'Moderna',
    heading: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    body: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  serif: {
    label: 'Elegante',
    heading: "'Playfair Display', Georgia, 'Times New Roman', serif",
    body: "'Lora', Georgia, serif",
  },
  rounded: {
    label: 'Amable',
    heading: "'Quicksand', 'Trebuchet MS', system-ui, sans-serif",
    body: "'Quicksand', 'Trebuchet MS', system-ui, sans-serif",
  },
  geometrica: {
    label: 'Geométrica',
    heading: "'Poppins', 'Century Gothic', system-ui, sans-serif",
    body: "'Poppins', 'Segoe UI', system-ui, sans-serif",
  },
  display: {
    label: 'Titular',
    heading: "'Montserrat', 'Segoe UI', system-ui, sans-serif",
    body: "'Inter', 'Segoe UI', system-ui, sans-serif",
  },
  clasica: {
    label: 'Clásica',
    heading: "'Lora', Georgia, 'Times New Roman', serif",
    body: "'Lora', Georgia, serif",
  },
  mono: {
    label: 'Técnica',
    heading: "'Space Mono', 'Consolas', 'Courier New', monospace",
    body: "'Space Mono', 'Consolas', monospace",
  },
  condensada: {
    label: 'Compacta',
    heading: "'Oswald', 'Arial Narrow', 'Segoe UI', sans-serif",
    body: "'Oswald', 'Arial Narrow', sans-serif",
  },
  manuscrita: {
    label: 'Manuscrita',
    heading: "'Dancing Script', 'Segoe Script', cursive",
    body: "'Dancing Script', 'Segoe Script', cursive",
  },
};

export const PORTFOLIO_LAYOUTS: { id: PortfolioLayout; label: string; hint: string }[] = [
  { id: 'grid', label: 'Cuadrícula', hint: 'Cuadros iguales, 3 columnas' },
  { id: 'masonry', label: 'Mosaico', hint: 'Alturas variadas, estilo Pinterest' },
  { id: 'showcase', label: 'Ficha', hint: 'Cada obra con todas sus fotos' },
  { id: 'list', label: 'Lista', hint: 'Una fila por obra, ideal si hay muchas' },
  { id: 'featured', label: 'Destacado', hint: 'La primera obra en grande' },
];

export const PORTFOLIO_GROUPINGS: { id: PortfolioGrouping; label: string; hint: string }[] = [
  { id: 'none', label: 'Sin agrupar', hint: 'Todas las obras seguidas' },
  { id: 'year', label: 'Por año', hint: 'Agrupa por año de inicio' },
  { id: 'location', label: 'Por localidad', hint: 'Agrupa por ciudad de la obra' },
  { id: 'type', label: 'Por tipo de reforma', hint: 'Baño, cocina, fachada, local…' },
  { id: 'status', label: 'Por estado', hint: 'En curso, finalizado, parado…' },
  { id: 'client', label: 'Por cliente', hint: 'Agrupa por cliente de la obra' },
];

export const PORTFOLIO_THEMES: Record<PortfolioTheme, PortfolioThemePreset> = {
  clasico: {
    id: 'clasico',
    name: 'Clásico',
    hint: 'Azul profesional',
    accent: '#2563eb',
    bg: '#f4f6fb',
    surface: '#ffffff',
    text: '#0f172a',
    muted: '#64748b',
    border: '#e2e8f0',
    radius: '0.9rem',
    font: 'sans',
    hero: 'overlay',
    dark: false,
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal',
    hint: 'Blanco y negro',
    accent: '#111111',
    bg: '#ffffff',
    surface: '#ffffff',
    text: '#111111',
    muted: '#6b7280',
    border: '#e5e7eb',
    radius: '0.25rem',
    font: 'sans',
    hero: 'minimal',
    dark: false,
  },
  elegante: {
    id: 'elegante',
    name: 'Elegante',
    hint: 'Serif y dorado',
    accent: '#a9842f',
    bg: '#f6f3ec',
    surface: '#fffdf8',
    text: '#201d16',
    muted: '#7c7463',
    border: '#e7dfcd',
    radius: '0.4rem',
    font: 'serif',
    hero: 'banner',
    dark: false,
  },
  industrial: {
    id: 'industrial',
    name: 'Industrial',
    hint: 'Oscuro y naranja',
    accent: '#f97316',
    bg: '#0e0f13',
    surface: '#171921',
    text: '#f4f4f5',
    muted: '#a1a1aa',
    border: '#2a2b33',
    radius: '0.3rem',
    font: 'sans',
    hero: 'overlay',
    dark: true,
  },
  calido: {
    id: 'calido',
    name: 'Cálido',
    hint: 'Terracota y crema',
    accent: '#c2571f',
    bg: '#faf4ee',
    surface: '#ffffff',
    text: '#2c1f17',
    muted: '#87705f',
    border: '#edded1',
    radius: '1.1rem',
    font: 'serif',
    hero: 'banner',
    dark: false,
  },
  oceano: {
    id: 'oceano',
    name: 'Océano',
    hint: 'Azul claro',
    accent: '#0284c7',
    bg: '#eff8ff',
    surface: '#ffffff',
    text: '#0c4a6e',
    muted: '#557e92',
    border: '#d4e8f5',
    radius: '1.2rem',
    font: 'sans',
    hero: 'overlay',
    dark: false,
  },
  bosque: {
    id: 'bosque',
    name: 'Bosque',
    hint: 'Verde natural',
    accent: '#2f7d55',
    bg: '#f2f8f3',
    surface: '#ffffff',
    text: '#1c3a2a',
    muted: '#63806f',
    border: '#d6e6da',
    radius: '0.9rem',
    font: 'rounded',
    hero: 'overlay',
    dark: false,
  },
  noir: {
    id: 'noir',
    name: 'Noir',
    hint: 'Oscuro y oro',
    accent: '#d4af37',
    bg: '#0b0b0f',
    surface: '#15151c',
    text: '#f4f4f6',
    muted: '#9b9ba6',
    border: '#26262f',
    radius: '0.4rem',
    font: 'serif',
    hero: 'minimal',
    dark: true,
  },
  vibrante: {
    id: 'vibrante',
    name: 'Vibrante',
    hint: 'Violeta vivo',
    accent: '#7c3aed',
    bg: '#faf5ff',
    surface: '#ffffff',
    text: '#2e1065',
    muted: '#7a6b99',
    border: '#ece6fb',
    radius: '1.3rem',
    font: 'rounded',
    hero: 'banner',
    dark: false,
  },
  moderno: {
    id: 'moderno',
    name: 'Moderno',
    hint: 'Índigo limpio',
    accent: '#4f46e5',
    bg: '#f5f6fa',
    surface: '#ffffff',
    text: '#111827',
    muted: '#6b7280',
    border: '#e5e7eb',
    radius: '0.7rem',
    font: 'sans',
    hero: 'banner',
    dark: false,
  },
};

export const PORTFOLIO_THEME_LIST = Object.values(PORTFOLIO_THEMES);
export const DEFAULT_THEME: PortfolioTheme = 'clasico';

function normalizeHex(value?: string): string | null {
  if (!value) return null;
  const hex = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
  }
  return null;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = normalizeHex(hex) || '#000000';
  return [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Mezcla `hex` con `target` en la proporción `amount` (0-1). */
function mix(hex: string, target: string, amount: number): string {
  const [r1, g1, b1] = hexToRgb(hex);
  const [r2, g2, b2] = hexToRgb(target);
  return rgbToHex(
    r1 + (r2 - r1) * amount,
    g1 + (g2 - g1) * amount,
    b1 + (b2 - b1) * amount,
  );
}

/** Devuelve el color de texto con mejor contraste sobre `hex`. */
function readableOn(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? '#111111' : '#ffffff';
}

export interface ResolvedPortfolioTheme {
  preset: PortfolioThemePreset;
  vars: Record<string, string>;
  hero: 'overlay' | 'banner' | 'minimal';
  layout: PortfolioLayout;
  grouping: PortfolioGrouping;
  accent: string;
  dark: boolean;
}

/** Resuelve el tema final aplicando los ajustes del administrador. */
export function resolvePortfolioTheme(
  config: Pick<PortfolioConfig, 'theme' | 'accentColor' | 'font' | 'layout' | 'grouping'>
): {
  preset: PortfolioThemePreset;
  vars: Record<string, string>;
  hero: PortfolioThemePreset['hero'];
  layout: PortfolioLayout;
  grouping: PortfolioGrouping;
} {
  const preset = PORTFOLIO_THEMES[config.theme ?? DEFAULT_THEME] ?? PORTFOLIO_THEMES[DEFAULT_THEME];
  const font = PORTFOLIO_FONTS[config.font ?? preset.font] ?? PORTFOLIO_FONTS.sans;
  const accent = normalizeHex(config.accentColor) || preset.accent;
  const accentContrast = readableOn(accent);
  const accentSoft = mix(accent, preset.dark ? '#000000' : '#ffffff', preset.dark ? 0.78 : 0.85);

  const vars: Record<string, string> = {
    '--pf-accent': accent,
    '--pf-accent-contrast': accentContrast,
    '--pf-accent-soft': accentSoft,
    '--pf-bg': preset.bg,
    '--pf-surface': preset.surface,
    '--pf-text': preset.text,
    '--pf-muted': preset.muted,
    '--pf-border': preset.border,
    '--pf-radius': preset.radius,
    '--pf-heading-font': font.heading,
    '--pf-body-font': font.body,
  };

  return {
    preset,
    vars,
    hero: preset.hero,
    layout: config.layout ?? 'grid',
    grouping: config.grouping ?? 'none',
  };
}

export type ResolvedPortfolioThemeInfo = ReturnType<typeof resolvePortfolioTheme>;

/** Utilidad para que el panel de administración pueda previsualizar el tema. */
export function themePreviewVars(config: Pick<PortfolioConfig, 'theme' | 'accentColor' | 'font'>): Record<string, string> {
  return resolvePortfolioTheme(config).vars;
}
